import classnames from "classnames";
import { ComponentPropsWithoutRef, PropsWithChildren, useState } from "react";
import Spinner from "./Spinner";

type Props = ComponentPropsWithoutRef<"button"> &
  PropsWithChildren<{
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    primary?: boolean;
    alert?: boolean;
    secondary?: boolean;
    danger?: boolean;
    size?: "sm" | "md" | "lg";
    loading?: boolean;
  }>;

export default function Button({
  children,
  onClick,
  className,
  disabled,
  loading = false,
  primary = false,
  alert = false,
  secondary = true,
  danger = false,
  size = "md",
  ...rest
}: Props) {
  const [spinnerShown, setSpinnerShown] = useState(false);

  return (
    <button
      className={classnames(
        "flex justify-center items-center whitespace-nowrap rounded-lg disabled:cursor-not-allowed cursor-pointer relative",
        getClassNameByType(),
        className,
      )}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      <Spinner
        show={loading}
        onDebouncedChange={(on) => setSpinnerShown(on)}
        parentClassName={classnames(getSpinnerClassNameByType(), "absolute")}
      />
      <div className={spinnerShown ? "invisible" : ""}>{children}</div>
    </button>
  );

  function getSpinnerClassNameByType() {
    let sizeClasses = "size-6";

    switch (size) {
      case "sm":
        sizeClasses = "size-4";
        break;
      case "md":
        sizeClasses = "size-5";
        break;
      case "lg":
        sizeClasses = "size-6";
        break;
    }

    return sizeClasses;
  }

  function getClassNameByType() {
    let colorClasses = "";
    let sizeClasses = "text-sm";

    switch (size) {
      case "sm":
        sizeClasses = "px-2 py-1 text-xs";
        break;
      case "md":
        sizeClasses = "px-4 py-1.5 text-sm";
        break;
      case "lg":
        sizeClasses = "px-4 py-2 text-base";
        break;
    }

    if (disabled) {
      colorClasses = "bg-slate-300 hover:bg-slate-300 text-white";
    } else if (primary) {
      colorClasses = "bg-blue-600 text-white hover:bg-blue-500";
    } else if (alert) {
      colorClasses = "bg-red-100 text-red-800 hover:bg-red-50";
    } else if (danger) {
      colorClasses = "bg-white border border-red-5 text-red-5 hover:bg-red-50";
    } else if (secondary) {
      colorClasses =
        "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50";
    } else {
      colorClasses = "bg-white text-black hover:bg-gray-100";
    }

    return classnames(colorClasses, sizeClasses);
  }
}
